import { LogoA, LogoB, LogoC, LogoD, LogoE, LogoF, LogoG, LogoH } from "@/components/LogoProposals";

const PROPOSALS = [
  {
    id: "A",
    name: "Monogramme T·M",
    description: "Cercle avec initiales entrelacées. Style maison de luxe, signature couture. Idéal pour faire de l'image de marque haut de gamme.",
    Logo: LogoA,
    luxury: false,
  },
  {
    id: "B",
    name: "Wordmark éditorial",
    description: "Pure typographie, italique gold pour 'et Mer', vague décorative. Style magazine et presse haut de gamme. Très lisible et intemporel.",
    Logo: LogoB,
    luxury: false,
  },
  {
    id: "C",
    name: "Vague stylisée",
    description: "Symbole minimaliste de 3 lignes (terre, mer, sable). Évoque la mer sans cliché. Style contemporain, très versatile.",
    Logo: LogoC,
    luxury: false,
  },
  {
    id: "D",
    name: "Cartouche TM",
    description: "Petit carré avec initiales + wordmark. Style startup premium, type Aesop ou Frama. Très moderne et facile à utiliser sur tous supports.",
    Logo: LogoD,
    luxury: false,
  },
  {
    id: "E",
    name: "Crête héraldique",
    description: "Couronne de vagues + 3 étoiles + monogramme T·m centré, baseline « Maison de Conciergerie ». Style palace 5 étoiles, hôtel particulier. Le plus prestigieux.",
    Logo: LogoE,
    luxury: true,
  },
  {
    id: "F",
    name: "Monogramme entrelacé",
    description: "T & M en lettres ultra-fines (style Didot/Bodoni) avec ampersand italique gold + traits décoratifs. Style haute joaillerie, parfum de luxe (type Cartier, Chanel n°5).",
    Logo: LogoF,
    luxury: true,
  },
  {
    id: "G",
    name: "Cachet circulaire « EST. »",
    description: "Sceau rond avec texte courbe « TERRE ET MER CONCIERGERIE · BRETAGNE · CÔTE D'ÉMERAUDE », monogramme T·m central et mention EST. 2024. Style maison ancestrale, cognac de prestige, parfumerie historique.",
    Logo: LogoG,
    luxury: true,
  },
  {
    id: "H",
    name: "Hairline éditorial",
    description: "Lettres ultra-fines extra-spacées encadrées de filets dorés et étoile centrale, type page d'accueil Hermès ou Cartier. Aérien, intemporel, ultra-luxe.",
    Logo: LogoH,
    luxury: true,
  },
];

const Logos = () => {
  return (
    <div className="bg-sand min-h-screen pb-32" data-testid="page-logos">
      <div className="container-tm pt-12 pb-16">
        <span className="overline">Atelier identité visuelle</span>
        <h1 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-4xl">
          8 propositions de logo
          <span className="italic text-gold"> modernes & luxe</span>.
        </h1>
        <p className="mt-6 font-sans text-base md:text-lg text-ink/70 leading-relaxed max-w-3xl">
          Concepts A–D : modernes et polyvalents. Concepts E–H : <span className="text-gold font-semibold">collection luxe</span> (palace, haute joaillerie, maison ancestrale, hairline éditorial). Tous alignés avec l'identité du site (Fraunces + Outfit, navy océan + amber).
        </p>
      </div>

      <div className="container-tm space-y-12">
        {PROPOSALS.map(({ id, name, description, Logo, luxury }) => (
          <section
            key={id}
            data-testid={`logo-proposal-${id}`}
            className="bg-white rounded-3xl border border-ink/8 shadow-soft overflow-hidden"
          >
            {luxury && (
              <div className="bg-gradient-to-r from-gold via-gold-300 to-gold px-6 py-2.5 text-center">
                <span className="text-[10px] uppercase tracking-[0.35em] font-bold text-white">
                  ✦  Collection Luxe  ✦
                </span>
              </div>
            )}
            <div className="grid lg:grid-cols-12">
              {/* Description */}
              <div className="lg:col-span-3 p-8 lg:p-10 bg-cream/40 border-r border-ink/8 flex flex-col">
                <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-gold">
                  Concept {id}
                </span>
                <h2 className="mt-3 font-serif text-3xl text-ink leading-tight">
                  {name}
                </h2>
                <p className="mt-4 font-sans text-sm text-ink/65 leading-relaxed flex-1">
                  {description}
                </p>
              </div>

              {/* Previews */}
              <div className="lg:col-span-9">
                {/* Light variant */}
                <div className="p-10 lg:p-14 border-b border-ink/8 flex items-center justify-center min-h-[180px]">
                  <Logo variant="dark" className="h-24 w-auto" />
                </div>

                {/* Dark variant */}
                <div className="bg-ink p-10 lg:p-14 flex items-center justify-center min-h-[180px] grain-overlay relative">
                  <Logo variant="light" className="h-24 w-auto relative z-10" />
                </div>

                {/* Header preview */}
                <div className="bg-white border-t border-ink/8 px-8 py-5 flex items-center justify-between">
                  <Logo variant="dark" className="h-12 w-auto" />
                  <div className="hidden md:flex items-center gap-8">
                    <span className="text-xs uppercase tracking-[0.22em] text-ink/60 font-semibold">Accueil</span>
                    <span className="text-xs uppercase tracking-[0.22em] text-ink/60 font-semibold">Services</span>
                    <span className="text-xs uppercase tracking-[0.22em] text-ink/60 font-semibold">Contact</span>
                    <span className="bg-sage text-white rounded-full px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] font-semibold">
                      Calculez vos revenus
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>

      <div className="container-tm mt-16">
        <div className="bg-ink rounded-3xl p-10 lg:p-12 text-white">
          <p className="overline text-gold mb-3">Comment choisir ?</p>
          <h3 className="font-serif text-2xl md:text-3xl leading-tight">
            Dites-moi simplement le concept (A à H) et je l'intègre dans le header, le footer et favicon en quelques minutes. On peut aussi mixer (ex. monogramme du E + wordmark du B, ou cachet G en favicon + wordmark H en header).
          </h3>
        </div>
      </div>
    </div>
  );
};

export default Logos;
